import React, { useState, useEffect } from 'react';
import { formatDateLong } from '../../../utils/formatDate';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { useLanguage } from '../../../contexts/LanguageContext';
import useEscapeKey from '../../../hooks/useEscapeKey';
import { formatCurrency } from '../../../utils/formatCurrency';
import paymentRequestService from '../../../services/paymentRequestService';
import { statusVariant } from '../../../utils/statusVariant';
import Badge from '../../../components/ui/Badge';

// ─── Status pill (read-only, no dropdown) ─────────────────────────────────────
const STATUS_CONFIG_STATIC = {
  draft:     { label: null, bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400'  },
  submitted: { label: null, bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500'  },
  approved:  { label: null, bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
};

const ReadOnlyStatusPill = ({ status, t }) => {
  const labels = {
    draft:     t('paymentRequests.status.draft')     || 'Draft',
    submitted: t('paymentRequests.status.submitted') || 'Submitted',
    approved:  t('paymentRequests.status.approved')  || 'Approved',
  };
  const cfg = STATUS_CONFIG_STATIC?.[status] || STATUS_CONFIG_STATIC?.draft;
  return (
    <Badge variant={statusVariant('paymentRequest', status)}>
      {labels?.[status] || status}
    </Badge>
  );
};

// ─── Locale-aware date helpers ─────────────────────────────────────────────────
const formatDateLocale = (dateStr, locale) => {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' })?.format(new Date(dateStr));
  } catch {
    return formatDateLong(dateStr, locale);
  }
};

const formatDateTimeLocale = (dateStr, locale) => {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })?.format(new Date(dateStr));
  } catch {
    return new Date(dateStr)?.toLocaleString();
  }
};

// ─── Section header (orange accent bar) ───────────────────────────────────────
const SectionHeader = ({ label }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="w-1 h-4 bg-primary rounded-full" />
    <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{label}</span>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────
const PaymentRequestViewModal = ({
  request,
  project,
  projectId,
  onClose,
  onEdit,
  onExportPDF,
  pdfExporting,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = language === 'fr' ? 'fr-CA' : 'en-CA';

  // Lecture seule : Echap ferme directement.
  useEscapeKey(onClose, true);

  const [items, setItems] = useState([]);
  const [values, setValues] = useState([]);
  const [loadingLines, setLoadingLines] = useState(true);

  // ─── Load contract lines at mount ─────────────────────────────────────────
  useEffect(() => {
    if (!request?.id || !projectId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingLines(true);
      try {
        const [fetchedValues, fetchedItems] = await Promise.all([
          paymentRequestService?.getValuesByRequestId(request?.id),
          paymentRequestService?.getItemsByProjectId(projectId),
        ]);
        if (!cancelled) {
          setValues(fetchedValues || []);
          setItems(fetchedItems || []);
        }
      } catch {
        // silently ignore — lines section will show empty
      } finally {
        if (!cancelled) setLoadingLines(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [request?.id, projectId]);

  // ─── Build enriched lines ──────────────────────────────────────────────────
  const valueMap = {};
  values?.forEach(v => { valueMap[v?.item_id] = v; });

  const isDP1 = request?.number === 1;

  const baseLines = items?.filter(i => !i?.is_extra)?.map(item => {
    const val = valueMap?.[item?.id];
    return {
      id: item?.id,
      description: item?.description || '',
      contractAmount: parseFloat(item?.contract_amount) || 0,
      previousAmount: parseFloat(val?.previous_amount) || 0,
      currentAmount:  parseFloat(val?.current_amount)  || 0,
      monthlyAmount:  parseFloat(val?.monthly_amount)  || 0,
    };
  });

  const extraLines = items?.filter(i => i?.is_extra)?.map(item => {
    const val = valueMap?.[item?.id];
    return {
      id: item?.id,
      description: item?.description || '',
      extraCode: item?.extra_code || null,
      contractAmount: parseFloat(item?.contract_amount) || 0,
      previousAmount: parseFloat(val?.previous_amount) || 0,
      currentAmount:  parseFloat(val?.current_amount)  || 0,
      monthlyAmount:  parseFloat(val?.monthly_amount)  || 0,
    };
  });

  // ─── Amounts ───────────────────────────────────────────────────────────────
  const subtotal = values?.reduce((s, v) => s + (parseFloat(v?.monthly_amount) || 0), 0);
  const contractTotal = baseLines?.reduce((s, line) => s + (line?.contractAmount || 0), 0);
  const toDateTotal   = baseLines?.reduce((s, line) => s + (line?.currentAmount  || 0), 0);
  const previousTotal = baseLines?.reduce((s, line) => s + (line?.previousAmount || 0), 0);
  const retentionRate = request?.retention_rate ?? 10;
  const retention = subtotal * (retentionRate / 100);
  const taxableBase = subtotal - retention;
  const tps = taxableBase * 0.05;
  const tvq = taxableBase * 0.09975;
  const total = taxableBase + tps + tvq;
  const montantSansTaxes = subtotal - retention;

  const updaterName = request?.updater_name || request?.updater?.full_name || '—';

  // ─── Column header helper ──────────────────────────────────────────────────
  const ColHeader = ({ children, right = false }) => (
    <th className={`text-[10px] font-semibold text-gray-400 uppercase tracking-wider py-2 px-3 ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );

  const renderLinesTable = (lines, isExtra = false) => {
    if (!lines?.length) return null;
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {isExtra && <ColHeader>{t('paymentRequests.modal.description')}</ColHeader>}
              {!isExtra && <ColHeader>{t('paymentRequests.modal.description')}</ColHeader>}
              <ColHeader right>{t('paymentRequests.modal.contractAmount')}</ColHeader>
              {!isDP1 && <ColHeader right>{t('paymentRequests.modal.valueToDate')}</ColHeader>}
              {!isDP1 && <ColHeader right>{t('paymentRequests.modal.previous')}</ColHeader>}
              <ColHeader right>{t('paymentRequests.modal.thisRequest')}</ColHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {lines?.map((line) => (
              <tr key={line?.id} className="hover:bg-gray-50/50">
                <td className="py-2 px-3 text-gray-700 max-w-[200px]">
                  <span className="block truncate">{line?.description || '—'}</span>
                  {isExtra && line?.extraCode && (
                    <span className="text-[10px] text-orange-500 font-medium">{line?.extraCode}</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                  {formatCurrency(line?.contractAmount)}
                </td>
                {!isDP1 && (
                  <td className="py-2 px-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                    {formatCurrency(line?.currentAmount)}
                  </td>
                )}
                {!isDP1 && (
                  <td className="py-2 px-3 text-right text-gray-500 tabular-nums whitespace-nowrap">
                    {formatCurrency(line?.previousAmount)}
                  </td>
                )}
                <td className="py-2 px-3 text-right font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                  {formatCurrency(line?.monthlyAmount)}
                </td>
              </tr>
            ))}
            {!isExtra && (
              <tr className="border-t-2 border-gray-300">
                <td className="py-2 px-3 font-bold text-gray-900">TOTAL</td>
                <td className="py-2 px-3 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                  {formatCurrency(contractTotal)}
                </td>
                {!isDP1 && (
                  <td className="py-2 px-3 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                    {formatCurrency(toDateTotal)}
                  </td>
                )}
                {!isDP1 && (
                  <td className="py-2 px-3 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                    {formatCurrency(previousTotal)}
                  </td>
                )}
                <td className="py-2 px-3 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                  {formatCurrency(subtotal)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return createPortal(
    // Lecture seule : un clic a l'exterieur ferme la fiche.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Icon name="DollarSign" size={18} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                DP{request?.number}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">
                {project?.projectNumber ? `${project?.projectNumber} — ` : ''}{project?.name || ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* ── Title bar ── */}
        <div className="bg-gray-50 border-b border-gray-200 px-7 py-3 text-center flex-shrink-0">
          <span className="text-xs font-bold text-gray-700 tracking-[0.2em] uppercase">
            {(t('paymentRequests.view.title') || 'DEMANDE DE PAIEMENT')?.toUpperCase()}
          </span>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">

          {/* Section 1: Période + Statut */}
          <div className="grid grid-cols-2 gap-4">
            {/* Période */}
            <div>
              <SectionHeader label={t('paymentRequests.view.period') || (language === 'fr' ? 'Période' : 'Period')} />
              <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">{t('paymentRequests.modal.periodStartLabel') || 'Début'}</span>
                  <span className="text-xs font-medium text-gray-800">
                    {request?.period_start ? formatDateLocale(request?.period_start, locale) : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">{t('paymentRequests.modal.periodEndLabel') || 'Fin'}</span>
                  <span className="text-xs font-medium text-gray-800">
                    {request?.period_end ? formatDateLocale(request?.period_end, locale) : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Statut */}
            <div>
              <SectionHeader label={t('paymentRequests.table.status') || 'Statut'} />
              <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 flex items-center">
                <ReadOnlyStatusPill status={request?.status} t={t} />
              </div>
            </div>
          </div>

          {/* Section 2: Lignes contractuelles */}
          <div>
            <SectionHeader label={t('paymentRequests.modal.baseLines') || (language === 'fr' ? 'Lignes contractuelles' : 'Contract Lines')} />
            {loadingLines ? (
              <div className="flex items-center justify-center py-6">
                <Icon name="Loader2" size={20} className="animate-spin text-orange-400" />
              </div>
            ) : baseLines?.length > 0 ? (
              renderLinesTable(baseLines, false)
            ) : (
              <p className="text-xs text-gray-400 italic py-2">{language === 'fr' ? 'Aucune ligne contractuelle' : 'No contract lines'}</p>
            )}
          </div>

          {/* Section 3: Extras (if any) */}
          {!loadingLines && extraLines?.length > 0 && (
            <div>
              <SectionHeader label={t('paymentRequests.modal.extrasSection') || 'Extras'} />
              {renderLinesTable(extraLines, true)}
            </div>
          )}

          {/* Section 4: Montants */}
          <div>
            <SectionHeader label={t('paymentRequests.view.amounts') || (language === 'fr' ? 'Montants' : 'Amounts')} />
            <div className="bg-gray-50 border border-gray-100 rounded-lg overflow-hidden">
              <div className="divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-gray-600">{t('paymentRequests.modal.subtotal') || (language === 'fr' ? 'Sous-total' : 'Subtotal')}</span>
                  <span className="text-xs font-medium text-gray-800 tabular-nums">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-red-500">— {t('paymentRequests.modal.retention', { rate: retentionRate })}</span>
                  <span className="text-xs text-red-500 tabular-nums">({formatCurrency(retention)})</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-gray-600">{t('paymentRequests.modal.subtotalAfterHoldback') || (language === 'fr' ? 'Sous-total après retenue' : 'Subtotal after holdback')}</span>
                  <span className="text-xs font-medium text-gray-800 tabular-nums">{formatCurrency(taxableBase)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-gray-600">{t('paymentRequests.modal.tps') || 'TPS (5%)'}</span>
                  <span className="text-xs text-gray-700 tabular-nums">{formatCurrency(tps)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-xs text-gray-600">{t('paymentRequests.modal.tvq') || 'TVQ (9,975%)'}</span>
                  <span className="text-xs text-gray-700 tabular-nums">{formatCurrency(tvq)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 bg-white">
                  <span className="text-sm font-semibold text-gray-900">{t('paymentRequests.modal.total') || 'Total'}</span>
                  <span className="text-sm font-bold text-gray-900 tabular-nums">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Modifié par + date */}
          <div className="border-t border-gray-200 pt-4 space-y-1">
            <p className="text-xs text-gray-400">
              {language === 'fr'
                ? `Modifié par ${updaterName} — ${formatDateTimeLocale(request?.updated_at, locale)}`
                : `Modified by ${updaterName} — ${formatDateTimeLocale(request?.updated_at, locale)}`
              }
            </p>
          </div>
        </div>

        {/* ── Action footer ── */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {t('paymentRequests.view.close')}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onExportPDF?.(request)}
              disabled={pdfExporting}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {pdfExporting ? (
                <Icon name="Loader2" size={13} className="animate-spin" />
              ) : (
                <Icon name="Download" size={13} />
              )}
              {pdfExporting
                ? `${t('paymentRequests.view.exportPdf')}...`
                : t('paymentRequests.view.exportPdf')
              }
            </button>
            {request?.status === 'approved' ? (
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-lg cursor-default select-none">
                <Icon name="Lock" size={13} className="text-gray-400" />
                {t('paymentRequests.closedReadOnly')}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onEdit?.(request)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              >
                <Icon name="Edit2" size={13} />
                {t('paymentRequests.actions.edit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentRequestViewModal;
